import { useCallback, useMemo } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { duplicateFolder, type Folder } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { docPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";

import { ContextMenu } from "./ContextMenu";
import { useCreateInTree } from "./useCreateInTree";

import type { RenderContext } from "./FolderItem";
import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { DocumentTreeStore } from "@/store/DocumentTreeStore";
import type { RefObject } from "react";

export const FolderItemContextMenu = observer(function FolderItemContextMenu({
  documentTree,
  folder,
  rowRef,
  renderContext,
  onOpenChange,
}: {
  documentTree: DocumentTreeStore;
  folder: Folder;
  rowRef: RefObject<HTMLElement | null>;
  renderContext?: RenderContext;
  onOpenChange?: (open: boolean) => void;
}) {
  const t = useTranslations();
  const router = useRouter();
  const { classroom, dialog } = useStore();
  const { createDocumentInTree, createFolderInTree } = useCreateInTree("folder");
  const isStudent = classroom.isStudentIn(documentTree.team);

  const handleCreateNewFileInside = useCallback(
    // TODO: handle error here
    async (type: "document" | "folder") => {
      if (type === "document") {
        await createDocumentInTree(folder, 0);
      } else {
        await createFolderInTree(folder);
      }
      documentTree.openFolder(folder);
    },
    [createDocumentInTree, createFolderInTree, documentTree, folder],
  );

  const handleDuplicateFolder = useCallback(() => {
    // TODO: handle error here
    trackEvent("folder_creation_clicked", { source: "duplicate" });

    const { folder: parentFolder, currentIndex = 0 } = documentTree.getParentFolder(folder);
    const newFolderIndex = currentIndex + 2;
    // Don't await this, cause it can take long time to duplicate folder
    duplicateFolder(folder.uid, {
      parentUid: parentFolder?.uid,
      position: newFolderIndex,
    });
  }, [folder, documentTree]);

  const handleForkFolder = useCallback(() => {
    // TODO: handle error here
    trackEvent("fork_folder_clicked", { folder_id: folder.uid, source: "folder" });
    dialog.forkDialogStore.handleForkButtonClick({ folderUidToFork: folder.uid });
  }, [folder, dialog.forkDialogStore]);

  const handleDeleteFolder = useCallback(() => {
    if (!documentTree.trash) {
      return;
    }
    if (folder.files && documentTree.currentDocId) {
      const currentDocIsInDeletingFolder = documentTree.findFileInTree(
        folder.files || [],
        documentTree.currentDocId,
      );
      if (currentDocIsInDeletingFolder) {
        const adjacentDocument = documentTree.getAdjacentDocument(folder);
        if (adjacentDocument) {
          router.push(docPageUrl({ docId: adjacentDocument.uid }));
        }
      }
    }
    documentTree.updateFilePosition(folder.uid, documentTree.trash.uid, 0);
    documentTree.closeFolder(folder);
  }, [documentTree, folder, router]);

  let items: DropdownItem[] = [];

  const personalDocTreeMenuItems = useMemo(
    () =>
      [
        {
          title: t("documentTree.renameFolder"),
          icon: <Icon name="Rename" />,
          onClick: () => {
            runInAction(() => {
              documentTree.editFolderMap.set(folder);
            });
          },
        },
        {
          title: t("documentTree.duplicateFolder"),
          icon: <Icon name="Duplicate" />,
          onClick: handleDuplicateFolder,
        },
        {
          title: t("documentTree.forkFolder"),
          icon: <Icon name="Fork" />,
          onClick: handleForkFolder,
        },
        { type: "separator" },
        {
          title: t("documentTree.addDocument"),
          icon: <Icon name="Add" />,
          onClick: () => handleCreateNewFileInside("document"),
        },
        {
          title: t("documentTree.addFolder"),
          icon: <Icon name="AddFolder" />,
          onClick: () => handleCreateNewFileInside("folder"),
        },
        { type: "separator" },
        {
          title: t("documentTree.deleteFolder"),
          icon: <Icon name="Trash" />,
          onClick: handleDeleteFolder,
        },
      ] satisfies DropdownItem[],
    [
      t,
      handleCreateNewFileInside,
      handleDeleteFolder,
      handleDuplicateFolder,
      handleForkFolder,
      documentTree,
      folder,
    ],
  );

  const publicProfileDocTreeMenuItems = useMemo(
    () =>
      [
        {
          title: t("documentTree.forkFolder"),
          icon: <Icon name="Fork" />,
          onClick: handleForkFolder,
        },
      ] satisfies DropdownItem[],
    [t, handleForkFolder],
  );

  if (renderContext === "document-tree" && isStudent) {
    return null;
  }

  if (renderContext === "document-tree") {
    items = personalDocTreeMenuItems;
  } else if (renderContext === "public-profile") {
    items = publicProfileDocTreeMenuItems;
  }

  return (
    <ContextMenu
      items={items}
      title={folder.title || t("doc.documentTitle.placeholder")}
      rowRef={rowRef}
      onOpenChange={onOpenChange}
    />
  );
});
